import { useMemo, useRef, useState } from "react";
import {
  Download, Upload, Settings as SettingsIcon, Tags, Coins,
  ChevronLeft, ChevronRight, Layers, Magnet, Filter, UserSquare,
  Database, Plug, CheckCircle2, AlertCircle, RefreshCw, ExternalLink,
} from "lucide-react";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Select from "../ui/Select";
import ConfirmDialog from "../ui/ConfirmDialog";
import EditableList from "../ui/EditableList";
import { monthKeyFromDate, today } from "../../lib/dateUtils";
import { useIntegrationsStatus } from "../../hooks/useIntegrationsStatus";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthKeyFor = (year, monthIdx) => `${year}-${String(monthIdx + 1).padStart(2, "0")}`;

const SECTIONS = [
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "taxonomies", label: "Taxonomies", icon: Tags },
  { id: "currency", label: "Currency", icon: Coins },
  { id: "general", label: "General", icon: SettingsIcon },
  { id: "backup", label: "Backup", icon: Database },
];

export default function Settings({ state, dashboard, ghl }) {
  const integrations = useIntegrationsStatus();
  const fileRef = useRef(null);
  const [importPreview, setImportPreview] = useState(null);
  const [fxYear, setFxYear] = useState(new Date().getFullYear());
  const [draftRates, setDraftRates] = useState({});

  const fxByMonth = useMemo(() => {
    const map = new Map();
    (state.fxRates || []).forEach((r) => map.set(r.month, r.usdToEgp));
    return map;
  }, [state.fxRates]);

  const currentMonthKey = monthKeyFromDate(today());

  const commitRate = (monthKey) => {
    const draft = draftRates[monthKey];
    if (draft === undefined) return;
    const value = Number(draft);
    if (Number.isFinite(value) && value > 0) {
      dashboard.upsertFxRate(monthKey, value);
    } else if (draft === "") {
      dashboard.deleteFxRate(monthKey);
    }
    setDraftRates((d) => {
      const { [monthKey]: _, ...rest } = d;
      return rest;
    });
  };

  const handleExport = () => {
    const json = dashboard.exportData();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `unicamel-dashboard-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      JSON.parse(text);
      setImportPreview(text);
    } catch (err) {
      alert("Invalid JSON file");
    }
    e.target.value = "";
  };

  const jumpTo = (id) => {
    const el = document.getElementById(`settings-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const taxonomies = [
    {
      id: "departments",
      icon: Layers,
      label: "Departments",
      tone: "indigo",
      hint: "Group offers + expenses (e.g. Email Outreach, SEO).",
      items: state.settings.departments || [],
      onChange: (next) => dashboard.updateSettings({ departments: next }),
      placeholder: "e.g. Email Outreach",
    },
    {
      id: "acquisitionSources",
      icon: Magnet,
      label: "Acquisition sources",
      tone: "blue",
      hint: "Where new clients come from.",
      items: state.settings.acquisitionSources || [],
      onChange: (next) => dashboard.updateSettings({ acquisitionSources: next }),
      placeholder: "e.g. LinkedIn Outbound",
    },
    {
      id: "funnels",
      icon: Filter,
      label: "Funnels",
      tone: "emerald",
      hint: "Specific campaigns or funnels.",
      items: state.settings.funnels || [],
      onChange: (next) => dashboard.updateSettings({ funnels: next }),
      placeholder: "e.g. Tour Operators Q2",
    },
    {
      id: "clientTypes",
      icon: UserSquare,
      label: "Client types",
      tone: "amber",
      hint: "Type of business each client is.",
      items: state.settings.clientTypes || [],
      onChange: (next) => dashboard.updateSettings({ clientTypes: next }),
      placeholder: "e.g. Cruise line",
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-100 dark:border-[var(--border-soft)] pb-3">
        <div>
          <h2 className="font-display font-semibold text-xl text-brand-ink dark:text-[var(--text-primary)]">Settings</h2>
          <p className="text-xs text-brand-slate mt-0.5">Taxonomies, currency rates, defaults and backups.</p>
        </div>
        <nav className="flex flex-wrap gap-1">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => jumpTo(s.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-brand-slate hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] hover:text-brand-ink dark:hover:text-[var(--text-primary)]"
              >
                <Icon size={13} /> {s.label}
              </button>
            );
          })}
        </nav>
      </div>

      <section id="settings-integrations" className="space-y-3 scroll-mt-4">
        <div className="flex items-center gap-2">
          <Plug size={15} className="text-brand-slate" />
          <h3 className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">Integrations</h3>
          <span className="text-xs text-brand-slate">External systems feeding the dashboard.</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <IntegrationCard
            name="Stripe"
            tone="indigo"
            connected={integrations.stripe.connected}
            loading={integrations.stripe.loading}
            primary={integrations.stripe.count > 0 ? `${integrations.stripe.count.toLocaleString()} charges cached` : "No charges yet"}
            secondary={integrations.stripe.lastSyncedAt ? `Last synced ${new Date(integrations.stripe.lastSyncedAt).toLocaleString()}` : "Never synced"}
            manage={{ label: "Open Payments V2", href: "#paymentsV2" }}
            onRefresh={integrations.refreshStripe}
            error={integrations.stripe.error}
          />
          <IntegrationCard
            name="GoHighLevel"
            tone="emerald"
            connected={ghl ? !ghl.error : null}
            loading={ghl?.loading}
            primary={ghl?.invoices?.length ? `${ghl.invoices.length.toLocaleString()} invoices fetched` : "No invoices fetched"}
            secondary={ghl?.fetchedAt ? `Last synced ${new Date(ghl.fetchedAt).toLocaleString()}` : "Never synced this session"}
            manage={{ label: "Open Payments", href: "#payments" }}
            onRefresh={ghl?.refresh}
            error={ghl?.error}
          />
          <IntegrationCard
            name="QuickBooks"
            tone="blue"
            connected={integrations.qb.connected}
            loading={integrations.qb.loading}
            primary={integrations.qb.connected ? "Connected" : "Not connected"}
            secondary={integrations.qb.connected ? "Sync from the Transactions tab" : "Connect from the Transactions tab"}
            manage={{ label: "Open Transactions", href: "#transactions" }}
            onRefresh={integrations.refreshQb}
            error={integrations.qb.error}
          />
        </div>
      </section>

      <section id="settings-taxonomies" className="space-y-3 scroll-mt-4">
        <div className="flex items-center gap-2">
          <Tags size={15} className="text-brand-slate" />
          <h3 className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">Taxonomies</h3>
          <span className="text-xs text-brand-slate">Lists that power dropdowns across the app.</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {taxonomies.map((t) => (
            <TaxonomyCard key={t.id} {...t} />
          ))}
        </div>
      </section>

      <section id="settings-currency" className="space-y-3 scroll-mt-4">
        <Card title={<span className="inline-flex items-center gap-2"><Coins size={15} /> Currency exchange (USD → EGP)</span>}>
          <p className="text-sm text-brand-slate mb-3">
            Enter the USD → EGP rate for each month — typically on the day you pay salaries. Edit a cell, hit Tab or Enter to save.
          </p>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFxYear((y) => y - 1)}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate"
                title="Previous year"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="font-display font-semibold text-base">{fxYear}</span>
              <button
                onClick={() => setFxYear((y) => y + 1)}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate"
                title="Next year"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
          <div className="rounded-lg border border-gray-100 dark:border-[var(--border-soft)] overflow-x-auto">
            <table className="w-full text-xs table-fixed">
              <colgroup>
                <col style={{ width: "14%" }} />
                {Array.from({ length: 12 }).map((_, i) => <col key={i} style={{ width: `${86 / 12}%` }} />)}
              </colgroup>
              <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">
                    1 USD = ? EGP
                  </th>
                  {MONTH_LABELS.map((m, i) => {
                    const mKey = monthKeyFor(fxYear, i);
                    const isCurrent = mKey === currentMonthKey;
                    return (
                      <th key={m} className={`px-1 py-1.5 text-center font-medium uppercase tracking-wide text-[10px] ${isCurrent ? "bg-brand-blue-soft text-brand-blue-dark" : "text-brand-slate"}`}>
                        {m}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100 dark:border-[var(--border-soft)]">
                  <td className="px-2 py-1 font-medium border-r border-gray-100 dark:border-[var(--border-soft)] text-brand-slate text-xs">
                    Rate
                  </td>
                  {MONTH_LABELS.map((_, i) => {
                    const mKey = monthKeyFor(fxYear, i);
                    const isCurrent = mKey === currentMonthKey;
                    const savedRate = fxByMonth.get(mKey);
                    const draft = draftRates[mKey];
                    const displayValue = draft !== undefined ? draft : (savedRate != null ? String(savedRate) : "");
                    return (
                      <td key={mKey} className={`px-1 py-1 ${isCurrent ? "bg-brand-blue-soft/30" : ""}`}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={displayValue}
                          placeholder="—"
                          onChange={(e) => setDraftRates((d) => ({ ...d, [mKey]: e.target.value }))}
                          onBlur={() => commitRate(mKey)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          className="w-full px-1 py-1 text-center border border-transparent hover:border-gray-200 focus:border-brand-blue rounded text-xs bg-transparent dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                        />
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      <section id="settings-general" className="grid grid-cols-1 lg:grid-cols-2 gap-4 scroll-mt-4">
        <Card title={<span className="inline-flex items-center gap-2"><SettingsIcon size={15} /> General</span>}>
          <div className="space-y-3">
            <Select
              label="Payment matrix months ahead"
              value={state.settings.matrixMonthsAhead}
              onChange={(e) => dashboard.updateSettings({ matrixMonthsAhead: Number(e.target.value) })}
            >
              <option value={12}>12 months</option>
              <option value={18}>18 months</option>
              <option value={24}>24 months</option>
            </Select>
            <div>
              <span className="block text-xs font-medium text-brand-slate mb-1">Due-date presets (days)</span>
              <input
                type="text"
                defaultValue={state.settings.duePresets.join(", ")}
                onBlur={(e) => {
                  const parts = e.target.value.split(",").map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);
                  if (parts.length) dashboard.updateSettings({ duePresets: parts });
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)]"
              />
              <p className="text-xs text-brand-slate mt-1">Comma-separated. Used by AR scheduling form.</p>
            </div>
          </div>
        </Card>

        <Card id="settings-backup" title={<span className="inline-flex items-center gap-2"><Database size={15} /> Backup & restore</span>}>
          <div className="space-y-3">
            <p className="text-sm text-brand-slate">
              Export your full database (clients, offers, payments, team, tools, settings) as JSON. Importing replaces everything.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleExport}><Download size={14} /> Export full backup</Button>
              <Button variant="secondary" onClick={() => fileRef.current?.click()}>
                <Upload size={14} /> Import backup
              </Button>
              <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={handleFileChange} />
            </div>
          </div>
        </Card>
      </section>

      <ConfirmDialog
        open={!!importPreview}
        onClose={() => setImportPreview(null)}
        title="Replace all data?"
        message="Importing this file will replace your current clients, offers, payments, team, tools and settings. Continue?"
        confirmLabel="Import & replace"
        variant="primary"
        onConfirm={() => { dashboard.importData(importPreview); setImportPreview(null); }}
      />
    </div>
  );
}

// Status card for an external integration on the Settings page. Shows a
// dot for connected/not, primary stat (count or status), last sync time,
// a manage link to the relevant tab, and a refresh icon button.
function IntegrationCard({ name, tone, connected, loading, primary, secondary, manage, onRefresh, error }) {
  const tones = {
    indigo:  { ring: "border-indigo-200",  iconBg: "bg-indigo-50 text-indigo-700"  },
    blue:    { ring: "border-blue-200",    iconBg: "bg-blue-50 text-blue-700"      },
    emerald: { ring: "border-emerald-200", iconBg: "bg-emerald-50 text-emerald-700" },
  };
  const t = tones[tone] || tones.blue;
  const dotColor = loading
    ? "bg-gray-300 animate-pulse"
    : connected === true
      ? "bg-emerald-500"
      : connected === false
        ? "bg-red-400"
        : "bg-gray-300";
  return (
    <div className={`bg-white dark:bg-[var(--bg-surface)] rounded-xl border ${t.ring} dark:border-[var(--border-soft)] shadow-card p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${t.iconBg}`}>
            <Plug size={14} />
          </span>
          <span className="font-display font-semibold text-sm text-brand-ink dark:text-[var(--text-primary)]">{name}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${dotColor}`} title={connected === true ? "Connected" : connected === false ? "Not connected" : "Checking..."} />
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={loading}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate"
              title="Re-check status"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            </button>
          )}
        </div>
      </div>
      <div className="space-y-0.5">
        <div className="flex items-center gap-1.5">
          {connected === true && <CheckCircle2 size={13} className="text-emerald-600" />}
          {connected === false && <AlertCircle size={13} className="text-red-500" />}
          <span className="text-sm font-medium text-brand-ink dark:text-[var(--text-primary)]">{primary}</span>
        </div>
        <div className="text-xs text-brand-slate">{secondary}</div>
        {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      </div>
      {manage && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-[var(--border-soft)]">
          <a
            href={manage.href}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline"
          >
            {manage.label} <ExternalLink size={11} />
          </a>
        </div>
      )}
    </div>
  );
}

// Colored card wrapper around EditableList. Tones map to the brand palette
// so each taxonomy is scannable at a glance.
function TaxonomyCard({ icon: Icon, label, tone, hint, items, onChange, placeholder }) {
  const tones = {
    indigo:  { ring: "border-indigo-200",  iconBg: "bg-indigo-50 text-indigo-700",  badge: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    blue:    { ring: "border-blue-200",    iconBg: "bg-blue-50 text-blue-700",      badge: "bg-blue-50 text-blue-700 border-blue-200" },
    emerald: { ring: "border-emerald-200", iconBg: "bg-emerald-50 text-emerald-700", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    amber:   { ring: "border-amber-200",   iconBg: "bg-amber-50 text-amber-700",    badge: "bg-amber-50 text-amber-700 border-amber-200" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div className={`bg-white dark:bg-[var(--bg-surface)] rounded-xl border ${t.ring} dark:border-[var(--border-soft)] shadow-card p-4`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${t.iconBg}`}>
            <Icon size={14} />
          </span>
          <span className="font-display font-semibold text-sm text-brand-ink dark:text-[var(--text-primary)]">{label}</span>
        </div>
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${t.badge}`}>
          {items.length}
        </span>
      </div>
      {hint && <p className="text-xs text-brand-slate mb-3">{hint}</p>}
      <EditableList
        label=""
        items={items}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>
  );
}
